import { useState, useEffect } from 'react';
import { invoicesApi, itemsApi, lookupApi } from '../api';
import { useData, submitForm } from '../hooks/useData';
import { Spinner, Modal, PageHeader, Field, Empty, EditBtn, CancelBtn, PdfBtn, Badge, Icons } from '../components/common';
import toast from 'react-hot-toast';
import { getBDDate } from '../utils/dateUtils';
import { downloadReport } from '../utils/downloadUtils';

const EMPTY_HDR = {
  inv_number:'', inv_date: getBDDate(),
  customer_name:'', customer_mobile:'', address:'',
  delivery_charge:'0', advance:'0', status:'created'
};
const EMPTY_LINE = { item_id:'', unit_price:0, discount_percent:0, discounted_price:0, quantity:1, amount:0 };

export default function InvoicesPage() {
  // Stop the browser's default "scroll to change value" behavior on number
  // inputs — only manually typed values should count.
  useEffect(() => {
    const blurNumberOnWheel = () => {
      if (document.activeElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    document.addEventListener('wheel', blurNumberOnWheel, { passive: true });
    return () => document.removeEventListener('wheel', blurNumberOnWheel);
  }, []);

  const { data, loading, reload } = useData(invoicesApi.list);
  const { data: allItems }        = useData(itemsApi.active);
  const { data: statuses }        = useData(lookupApi.statuses);
  const [modal, setModal]         = useState(null);
  const [hdr, setHdr]             = useState(EMPTY_HDR);
  const [lines, setLines]         = useState([{...EMPTY_LINE}]);
  const [errors, setErrors]       = useState({});
  const [search, setSearch]       = useState('');
  const [availMap, setAvailMap]   = useState({}); // item_id -> { available_stock, physical_stock, reserved_qty }
  const [saving, setSaving]       = useState(false);
  const [exporting, setExporting] = useState(false);
  // Customer order/item history lookup (by mobile number)
  const [custStats, setCustStats]        = useState(null); // { order_count, item_count }
  const [custStatsLoading, setCustStatsLoading] = useState(false);

  // Load live available stock (excluding this invoice's own reservation when editing)
  const loadAvailability = async (excludeInvId = null) => {
    try {
      const res = await invoicesApi.availableStock(excludeInvId);
      const map = {};
      res.data.data.forEach(row => { map[row.id] = row; });
      setAvailMap(map);
    } catch { /* non-blocking */ }
  };

  // Look up how many orders / items this mobile number has purchased before
  const lookupCustomerStats = async (mobile) => {
    const m = (mobile || '').trim();
    if (m.length < 5) { setCustStats(null); return; }
    setCustStatsLoading(true);
    try {
      const res = await invoicesApi.customerStats(m);
      setCustStats(res.data.data);
    } catch {
      setCustStats(null);
    } finally {
      setCustStatsLoading(false);
    }
  };

  const openAdd = async () => {
    setHdr(EMPTY_HDR); setLines([{...EMPTY_LINE}]); setErrors({}); setCustStats(null);
    loadAvailability();
    setModal('add');
    // Auto-fill next invoice number (e.g. INV5) — still editable manually
    try {
      const res  = await invoicesApi.nextNumber();
      const next = res?.data?.data?.next_number;
      if (next) setHdr(h => ({ ...h, inv_number: next }));
    } catch { /* non-blocking — user can type the invoice number manually */ }
  };

  const openEdit = async (row) => {
    try {
      const res = await invoicesApi.getOne(row.id);
      const inv = res.data.data;
      setHdr({...inv});
      setLines(inv.details?.length ? inv.details.map(d => {
        const unit = parseFloat(d.unit_price) || 0;
        const pct  = parseFloat(d.discount_percent) || 0;
        return {
          ...d,
          discounted_price: Math.round(unit * (1 - pct/100) * 100) / 100,
          amount: parseFloat(d.amount) || 0, // real stored value now — not recomputed
        };
      }) : [{...EMPTY_LINE}]);
      setErrors({});
      loadAvailability(row.id);
      if (inv.customer_mobile) lookupCustomerStats(inv.customer_mobile);
      setModal('edit');
    } catch { toast.error('Failed to load invoice'); }
  };
  const close = () => { setModal(null); setErrors({}); setAvailMap({}); setCustStats(null); };
  const setH  = e => setHdr(h => ({...h, [e.target.name]: e.target.value}));

  // Mobile field — also clears stale customer stats until the next blur-lookup
  const setCustomerMobile = e => { setH(e); setCustStats(null); };

  const handleItemChange = (idx, itemId) => {
    const item  = allItems.find(i => String(i.id)===String(itemId));
    const price = item?.price || 0;
    setLines(ls => ls.map((l,i) => i===idx
      ? {...l, item_id: itemId, unit_price: price, discount_percent:0, discounted_price: price, quantity:1, amount: price}
      : l
    ));
  };

  // ── Unit Price / Discounted Price / Discount % / Quantity / Amount —
  // kept in sync, any one can drive the others. Amount is now a real,
  // directly-editable field (invoice_detail.amount is a normal writable
  // column, not DB-generated); editing it reverse-derives Discount % so
  // everything stays consistent.
  const setLineUnitPrice = (idx, val) => {
    setLines(ls => ls.map((l,i) => {
      if (i!==idx) return l;
      const unit = parseFloat(val) || 0;
      const pct  = parseFloat(l.discount_percent) || 0;
      const qty  = parseFloat(l.quantity) || 0;
      const discounted = unit * (1 - pct/100);
      const discountedRounded = unit ? Math.round(discounted*100)/100 : 0;
      return { ...l, unit_price: val, discounted_price: discountedRounded, amount: Math.round(discountedRounded*qty*100)/100 };
    }));
  };
  const setLineDiscountedPrice = (idx, val) => {
    setLines(ls => ls.map((l,i) => {
      if (i!==idx) return l;
      const unit = parseFloat(l.unit_price) || 0;
      const dp   = parseFloat(val) || 0;
      const qty  = parseFloat(l.quantity) || 0;
      const pct  = unit > 0 ? (1 - dp/unit) * 100 : 0;
      return { ...l, discounted_price: val, discount_percent: Math.round(pct*100)/100, amount: Math.round(dp*qty*100)/100 };
    }));
  };
  const setLineDiscountPercent = (idx, val) => {
    setLines(ls => ls.map((l,i) => {
      if (i!==idx) return l;
      const unit = parseFloat(l.unit_price) || 0;
      const pct  = parseFloat(val) || 0;
      const qty  = parseFloat(l.quantity) || 0;
      const discounted = unit * (1 - pct/100);
      const discountedRounded = unit ? Math.round(discounted*100)/100 : 0;
      return { ...l, discount_percent: val, discounted_price: discountedRounded, amount: Math.round(discountedRounded*qty*100)/100 };
    }));
  };
  const setLineQuantity = (idx, val) => {
    setLines(ls => ls.map((l,i) => {
      if (i!==idx) return l;
      const qty        = parseFloat(val) || 0;
      const discounted = parseFloat(l.discounted_price) || 0;
      return { ...l, quantity: val, amount: Math.round(discounted*qty*100)/100 };
    }));
  };
  // Direct edit to Amount — reverse-derives Discounted Price and Discount %
  // so the line stays internally consistent (unit_price and quantity unchanged)
  const setLineAmount = (idx, val) => {
    setLines(ls => ls.map((l,i) => {
      if (i!==idx) return l;
      const unit = parseFloat(l.unit_price) || 0;
      const qty  = parseFloat(l.quantity) || 0;
      const amt  = parseFloat(val) || 0;
      if (qty <= 0) return { ...l, amount: val };
      const impliedDiscountedPrice = amt / qty;
      const pct = unit > 0 ? (1 - impliedDiscountedPrice/unit) * 100 : 0;
      return {
        ...l,
        amount: val,
        discounted_price: Math.round(impliedDiscountedPrice*100)/100,
        discount_percent: Math.round(pct*100)/100,
      };
    }));
  };

  const addLine = () => setLines(ls => [...ls, {...EMPTY_LINE}]);
  const delLine = idx => setLines(ls => ls.filter((_,i) => i!==idx));

  const subtotal   = lines.reduce((s,l) => s+(parseFloat(l.amount)||0), 0);
  const grandTotal = subtotal + parseFloat(hdr.delivery_charge||0);
  const due        = grandTotal - parseFloat(hdr.advance||0);

  // Aggregate requested qty per item across all lines (an item could appear twice)
  const requestedByItem = lines.reduce((acc, l) => {
    if (!l.item_id) return acc;
    acc[l.item_id] = (acc[l.item_id] || 0) + (parseInt(l.quantity) || 0);
    return acc;
  }, {});

  // Per-line stock warning: does this item's TOTAL requested qty exceed available?
  const lineWarning = (itemId) => {
    if (!itemId || !availMap[itemId]) return null;
    const avail = availMap[itemId].available_stock;
    const requested = requestedByItem[itemId] || 0;
    if (requested > avail) {
      return `Only ${Math.max(0, avail)} available (requested ${requested})`;
    }
    return null;
  };

  const hasStockWarnings = Object.keys(requestedByItem).some(id => lineWarning(id));

  const validate = () => {
    const e = {};
    if (!hdr.customer_name?.trim())  e.customer_name   = 'Required';
    if (!hdr.inv_number?.trim())     e.inv_number      = 'Required';
    if (lines.some(l => !l.item_id)) e.lines = 'Select an item for every line';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doSave = async (forceOversell = false) => {
    const payload = { ...hdr, details: lines, force_oversell: forceOversell };
    setSaving(true);
    try {
      if (modal === 'add') {
        await invoicesApi.create(payload);
        toast.success('Invoice created');
      } else {
        await invoicesApi.update(hdr.id, payload);
        toast.success('Invoice updated');
      }
      reload();
      close();
    } catch (err) {
      const res = err?.response?.data;
      if (res?.stock_issues?.length) {
        // Backend caught an oversell — show details and offer to proceed anyway
        const lines = res.stock_issues.map(i =>
          `${i.item_name}: requested ${i.requested}, only ${i.available} available`
        ).join('\n');
        toast.error(
          (t) => (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Not enough stock available</div>
              <div style={{ fontSize: '.82rem', whiteSpace: 'pre-line', marginBottom: 8 }}>{lines}</div>
              <button
                className="btn btn-secondary btn-xs"
                onClick={() => { toast.dismiss(t.id); doSave(true); }}
              >
                Save anyway
              </button>
            </div>
          ),
          { duration: 8000 }
        );
      } else {
        toast.error(res?.message || 'Failed to save invoice');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!validate()) { toast.error('Please fill in all required fields'); return; }
    doSave(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadReport(invoicesApi.exportExcel, 'sales-invoices-list.xlsx');
    } catch {
      toast.error('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  const filtered = data.filter(r =>
    !search ||
    r.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.customer_mobile?.includes(search) ||
    r.inv_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Sales Invoices" subtitle="Create and manage customer invoices"
        action={<button className="btn btn-primary btn-sm" onClick={openAdd}>+ New Invoice</button>} />

      <div className="card">
        <div className="card-header">
          <div className="search-wrap">
            <Icons.Search size={14} className="search-icon" />
            <input type="text" className="search-input" placeholder="Search by customer, mobile or invoice #…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-secondary btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
        {loading ? <Spinner /> : (
          <div className="table-wrap">
            {filtered.length===0 ? <Empty msg="No invoices yet" /> : (
              <table className="responsive-table">
                <thead><tr>
                  <th>Invoice #</th><th>Date</th><th>Customer</th>
                  <th>Total</th><th>Due</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>{filtered.map(r => (
                  <tr key={r.id}>
                    <td data-label="Invoice #" className="td-primary font-mono">{r.inv_number}</td>
                    <td data-label="Date" className="text-muted">{r.inv_date}</td>
                    <td data-label="Customer">
                      <div className="td-primary">{r.customer_name}</div>
                      <div className="td-muted">{r.customer_mobile}</div>
                    </td>
                    <td data-label="Total" className="font-mono">৳{Number(r.total_price).toLocaleString()}</td>
                    <td data-label="Due" className="font-mono text-danger fw-500">৳{Number(r.due).toLocaleString()}</td>
                    <td data-label="Status"><Badge status={r.status} /></td>
                    <td data-label="Actions"><div className="action-cell">
                      <EditBtn onClick={()=>{
                        if (r.status === 'delivered' || r.status === 'returned') {
                          toast.error('This invoice has already been delivered and can no longer be edited');
                          return;
                        }
                        openEdit(r);
                      }} />
                      <CancelBtn onClick={()=>submitForm(()=>invoicesApi.cancel(r.id),'Invoice cancelled',reload)} label="Cancel Invoice" />
                      <PdfBtn href={invoicesApi.pdf(r.id)} />
                    </div></td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal==='add' ? 'New Invoice' : 'Edit Invoice'} onClose={close} size="modal-lg"
          footer={<>
            <button className="btn btn-secondary" onClick={close}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Invoice'}
            </button>
          </>}>

          <div className="form-row form-row-2">
            <Field label="Invoice #" required>
              <input type="text" name="inv_number" value={hdr.inv_number} onChange={setH}
                placeholder="e.g. INV1"
                style={errors.inv_number?{borderColor:'var(--c-danger)'}:{}} />
              {errors.inv_number && <span style={{fontSize:'.75rem',color:'var(--c-danger)'}}>Required</span>}
              {modal==='add' && (
                <span style={{fontSize:'.72rem',color:'var(--c-text-3)',marginTop:3,display:'block'}}>
                  Auto-generated — you can edit it if needed
                </span>
              )}
            </Field>
            <Field label="Invoice Date">
              <input type="date" name="inv_date" value={hdr.inv_date} onChange={setH} />
            </Field>
          </div>
          <div className="form-row form-row-2">
            <Field label="Customer Name" required>
              <input type="text" name="customer_name" value={hdr.customer_name} onChange={setH}
                placeholder="Full name"
                style={errors.customer_name?{borderColor:'var(--c-danger)'}:{}} />
              {errors.customer_name && <span style={{fontSize:'.75rem',color:'var(--c-danger)'}}>Required</span>}
            </Field>
            <Field label="Mobile">
              <input type="tel" name="customer_mobile" value={hdr.customer_mobile}
                onChange={setCustomerMobile}
                onBlur={() => lookupCustomerStats(hdr.customer_mobile)}
                placeholder="01XXXXXXXXX (optional)" />
              {(custStatsLoading || custStats) && (
                <div style={{ fontSize:'.74rem', color: custStats?.order_count ? 'var(--c-accent-dk)' : 'var(--c-text-3)', marginTop:4 }}>
                  {custStatsLoading
                    ? 'Checking customer history…'
                    : custStats.order_count > 0
                      ? `${custStats.order_count} previous order${custStats.order_count!==1?'s':''} · ${custStats.item_count} item${custStats.item_count!==1?'s':''} purchased`
                      : 'New customer — no previous orders'}
                </div>
              )}
            </Field>
          </div>
          <Field label="Delivery Address">
            <input type="text" name="address" value={hdr.address||''} onChange={setH} placeholder="Optional delivery address" />
          </Field>

          {/* Line items */}
          <div className="section-label" style={{marginTop:20}}>Items</div>
          {errors.lines && <div style={{fontSize:'.78rem',color:'var(--c-danger)',marginBottom:8}}>{errors.lines}</div>}
          {hasStockWarnings && (
            <div style={{ marginBottom: 10, padding: '10px 14px', background: 'var(--c-warning-lt)', border: '1px solid rgba(176,125,26,.2)', borderRadius: 'var(--r-sm)', fontSize: '.82rem', color: 'var(--c-warning)' }}>
              Some items exceed current available stock — see warnings below each line. You can still save, but delivery may be delayed.
            </div>
          )}
          <div style={{overflowX:'auto'}}>
            <table className="detail-table">
              <thead><tr>
                <th style={{minWidth:200}}>Item</th>
                <th style={{minWidth:100}}>Unit Price</th>
                <th style={{minWidth:110}}>Discounted Price</th>
                <th style={{minWidth:90}}>Discount %</th>
                <th style={{minWidth:80}}>Qty</th>
                <th style={{minWidth:100}}>Amount</th>
                <th style={{width:36}}></th>
              </tr></thead>
              <tbody>
                {lines.map((line,idx) => {
                  const warning = lineWarning(line.item_id);
                  const avail = availMap[line.item_id];
                  return (
                    <tr key={idx}>
                      <td>
                        <select value={line.item_id} onChange={e=>handleItemChange(idx,e.target.value)}>
                          <option value="">Select item…</option>
                          {allItems.map(i=><option key={i.id} value={i.id}>{i.item_name} ({i.brand_name})</option>)}
                        </select>
                        {avail && (
                          <div style={{ fontSize:'.72rem', color: warning ? 'var(--c-danger)' : 'var(--c-text-3)', marginTop:3 }}>
                            {warning || `${avail.available_stock} available`}
                          </div>
                        )}
                      </td>
                      <td><input type="number" value={line.unit_price} min="0" step="0.01"
                        onChange={e=>setLineUnitPrice(idx,e.target.value)} /></td>
                      <td><input type="number" value={line.discounted_price} min="0" step="0.01"
                        placeholder="e.g. 80"
                        onChange={e=>setLineDiscountedPrice(idx,e.target.value)} /></td>
                      <td><input type="number" value={line.discount_percent} min="0" max="100" step="0.01"
                        onChange={e=>setLineDiscountPercent(idx,e.target.value)} /></td>
                      <td><input type="number" value={line.quantity} min="1"
                        onChange={e=>setLineQuantity(idx,e.target.value)}
                        style={warning ? {borderColor:'var(--c-danger)'} : {}} /></td>
                      <td><input type="number" value={line.amount} min="0" step="0.01"
                        onChange={e=>setLineAmount(idx,e.target.value)}
                        style={{ fontWeight:500 }} /></td>
                      <td><button className="del-btn" onClick={()=>delLine(idx)} title="Remove"><Icons.Cancel size={13}/></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button className="btn btn-ghost btn-sm mt-8" onClick={addLine}>
            <Icons.Plus size={13} /> Add Line
          </button>

          {/* Totals */}
          <div style={{marginTop:16,padding:'14px 16px',background:'var(--c-bg)',borderRadius:'var(--r-md)',border:'1px solid var(--c-border)'}}>
            <div className="form-row form-row-3" style={{marginBottom:12}}>
              <Field label="Delivery Charge (৳)">
                <input type="number" name="delivery_charge" value={hdr.delivery_charge} onChange={setH} min="0" step="0.01" />
              </Field>
              <Field label="Advance Paid (৳)">
                <input type="number" name="advance" value={hdr.advance} onChange={setH} min="0" step="0.01" />
              </Field>
              <Field label="Status">
                <select name="status" value={hdr.status} onChange={setH}>
                  {statuses.map(s=><option key={s.id} value={s.status}>{s.status}</option>)}
                </select>
              </Field>
            </div>
            <div style={{borderTop:'1px solid var(--c-border)',paddingTop:10,display:'flex',flexDirection:'column',gap:4,alignItems:'flex-end',fontSize:'.875rem'}}>
              <div style={{color:'var(--c-text-2)'}}>Subtotal: <strong className="font-mono">৳{subtotal.toLocaleString()}</strong></div>
              <div style={{color:'var(--c-text-2)'}}>Grand Total: <strong className="font-mono">৳{grandTotal.toLocaleString()}</strong></div>
              <div style={{fontSize:'1rem',fontWeight:600,color:'var(--c-danger)'}}>Due: <span className="font-mono">৳{due.toLocaleString()}</span></div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}